/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Müşteri Paneli
 * ───────────────────────────────────────────────────────────────
 * Müşterilerin hem kendi hisselerini hem de katalogu görüntülediği sayfa
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Beef, CreditCard, MessageCircle, LogOut, User, Loader2, Tag, ShoppingCart, Info, ShieldAlert, ArrowLeft, Home } from 'lucide-react';
import { formatWhatsAppNumber } from '../../utils/phoneUtils';
import { collection, query, where, getDocs, documentId, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import CustomerTicketModal from '../../components/tickets/CustomerTicketModal';
import { getParentBranchName } from '../../utils/branchUtils';

export default function CustomerDashboard() {
  const { userProfile, signOut, systemSettings } = useAuth();
  
  // ── SEKMELER ──
  const [activeTab, setActiveTab] = useState('hesabim'); // 'hesabim' veya 'katalog'

  // ── HESABIM SEKMESİ STATE'LERİ ──
  const [shares, setShares] = useState([]);
  const [animals, setAnimals] = useState({});
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── KATALOG SEKMESİ STATE'LERİ ──
  const [catalogAnimals, setCatalogAnimals] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCatalogBranch, setSelectedCatalogBranch] = useState('all');

  // ── DESTEK STATE'LERİ ──
  const [tickets, setTickets] = useState([]);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedAnimalForTicket, setSelectedAnimalForTicket] = useState(null);

  useEffect(() => {
    if (!userProfile?.phone) return;
    const q = query(collection(db, 'tickets'), where('customerPhone', '==', userProfile.phone));
    const unsubscribe = onSnapshot(q, (snapshot) => {
       const tkts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
       setTickets(tkts);
    });
    return unsubscribe;
  }, [userProfile]);

  const handleOpenTicketModal = (animal) => {
     setSelectedAnimalForTicket(animal);
     setIsTicketModalOpen(true);
  };

  // ── 1. HESABIM VERİLERİNİ ÇEKME ──
  useEffect(() => {
    const fetchPersonalData = async () => {
      if (!userProfile?.phone) return;
      setLoading(true);

      try {
        const sharesQ = query(collection(db, 'shares'), where('customerPhone', '==', userProfile.phone));
        const sharesSnap = await getDocs(sharesQ);
        
        // Çift Yönlü Filtreleme: Aynı telefona sahip farklı müşteriler (farklı soyisimler) olabilir
        const userLastName = (userProfile.lastName || '').toLowerCase().trim();
        const sharesList = sharesSnap.docs
           .map(d => ({ id: d.id, ...d.data() }))
           .filter(s => (s.customerLastName || '').toLowerCase().trim() === userLastName);
        
        setShares(sharesList);

        if (sharesList.length > 0) {
          const animalIds = [...new Set(sharesList.map(s => s.animalId))];
          const animalsMap = {};
          
          for (let i = 0; i < animalIds.length; i += 10) {
             const chunk = animalIds.slice(i, i + 10);
             const animalsQ = query(collection(db, 'animals'), where(documentId(), 'in', chunk));
             const animalsSnap = await getDocs(animalsQ);
             animalsSnap.docs.forEach(doc => {
               animalsMap[doc.id] = { id: doc.id, ...doc.data() };
             });
          }
          setAnimals(animalsMap);

          const shareIds = sharesList.map(s => s.id);
          // Orijinal kodda payments koleksiyonundan sorgu atılıyordu,
          // lakin Firebase Rules müşteri girişi olmadığı için bunu engeller!
          // Bakiyeler zaten share.paidAmount içinde tutulduğu için bu koda ihtiyaç yoktur.
        }
      } catch (err) {
         console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonalData();
  }, [userProfile]);

  // ── 2. KATALOG VERİLERİNİ ÇEKME (SADECE KATALOG SEKMESİ AÇILINCA) ──
  useEffect(() => {
    if (activeTab === 'katalog' && catalogAnimals.length === 0) {
      loadCatalog();
    }
  }, [activeTab]);

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      // Tüm hayvanları çek
      const animalsSnap = await getDocs(query(collection(db, 'animals')));
      const allAnimals = animalsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Tüm hisseleri çek (Sadece adedini saymak için, isimleri GİZLİ)
      const sharesSnap = await getDocs(query(collection(db, 'shares')));
      const countMap = {};
      sharesSnap.docs.forEach(d => {
        const data = d.data();
        const animalId = data.animalId;
        const sharesBought = Number(data.shareWeight) || 1;
        countMap[animalId] = (countMap[animalId] || 0) + sharesBought;
      });
      
      const processed = allAnimals.map(anim => {
         const taken = countMap[anim.id] || 0;
         const remaining = anim.totalShares - taken;
         return { ...anim, takenShares: taken, remainingShares: remaining };
      }).filter(anim => anim.remainingShares > 0); // Hissesi dolan hayvanları gizle
      
      // Sıralama Algoritması: Kesim sırasına göre.
      processed.sort((a, b) => {
         const numA = parseInt(a.slaughterNo) || 99999;
         const numB = parseInt(b.slaughterNo) || 99999;
         return numA - numB;
      });
      
      setCatalogAnimals(processed);
    } catch (e) {
      console.error(e);
    } finally {
      setCatalogLoading(false);
    }
  };

  // ── HESAPLAMALAR VE ACTIONS ──
  const totalDebt = shares.reduce((acc, currShare) => acc + Number(currShare.price || 0), 0);

  const totalPaid = shares.reduce((acc, s) => acc + Number(s.paidAmount || 0), 0);
  const remainingDebt = totalDebt - totalPaid;

  const handleWhatsAppClick = (animal, isCatalog = false) => {
    let text = "";
    if (isCatalog) {
       text = `Merhaba, ${animal.tagNo} küpe nolu kurbanlıkta boşta olan hisselerden biriyle ilgileniyorum. Bilgi alabilir miyim?`;
    } else {
       text = `Merhaba, ben ${userProfile.firstName} ${userProfile.lastName}. ${animal ? `(${animal.tagNo} Küpe Nolu)` : 'Benim'} kurban hissemle ilgili size ulaşıyorum.`;
    }
    const companyPhone = "905373202533"; 
    const waLink = `https://wa.me/${formatWhatsAppNumber(companyPhone)}?text=${encodeURIComponent(text)}`;
    window.open(waLink, '_blank');
  };

  // ── RENDER: HESABIM ──
  const renderHesabim = () => (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">
          Hoş geldiniz, {userProfile?.firstName} {userProfile?.lastName} 👋
        </h2>
        <p className="text-navy-400 mt-1 text-sm">
          Hisse ve ödeme bilgileriniz aşağıdadır.
        </p>
      </div>

      {loading ? (
          <div className="flex flex-col items-center justify-center p-12 text-navy-400">
            <Loader2 className="w-8 h-8 animate-spin mb-4 text-forest-500" />
            <p>Kişisel verileriniz yükleniyor...</p>
          </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="glass rounded-2xl p-5 border border-navy-700/50 relative overflow-hidden">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-forest-600 to-forest-800 flex items-center justify-center relative z-10">
                  <Beef className="w-5 h-5 text-forest-400" />
                </div>
                <h3 className="font-semibold text-white relative z-10">Toplam Hissem</h3>
              </div>
              <p className="text-4xl font-bold text-white relative z-10">{shares.length}</p>
              <p className="text-sm text-navy-400 mt-1 relative z-10">aktif kayıtlı hisse</p>
            </div>

            <div className="glass rounded-2xl p-5 border border-navy-700/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-500/20 to-gold-700/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-gold-400" />
                </div>
                <h3 className="font-semibold text-white">Ödeme Durumu</h3>
              </div>
              <div className="flex flex-col">
                <div className="flex justify-between items-end mb-1">
                    <span className="text-xs text-navy-400 uppercase tracking-wider font-semibold">Ödenen</span>
                    <span className="text-xl font-bold text-forest-400">₺{totalPaid.toLocaleString('tr-TR')}</span>
                </div>
                <div className="w-full h-px bg-navy-700/50 my-2"></div>
                <div className="flex justify-between items-end">
                    <span className="text-xs text-navy-400 uppercase tracking-wider font-semibold">Kalan Borç</span>
                    <span className="text-xl font-bold text-danger-400">₺{remainingDebt > 0 ? remainingDebt.toLocaleString('tr-TR') : '0'}</span>
                </div>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-bold text-white mb-4">Size Ait Hisse Detayları</h3>
          
          {shares.length === 0 ? (
            <div className="glass rounded-2xl p-6 border border-navy-700/50">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Beef className="w-12 h-12 text-navy-600 mb-3 opacity-50" />
                <p className="text-navy-300 font-medium">Bize ulaşan bir hisse kaydınız bulunamadı.</p>
                {!systemSettings?.isCatalogClosed && (
                  <div className="mt-4">
                    <button onClick={() => setActiveTab('katalog')} className="px-4 py-2 bg-forest-600 text-white rounded-lg text-sm font-bold shadow-lg hover:bg-forest-500">
                       Kurban Kataloğunu İncele
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {shares.map((share) => {
                const animal = animals[share.animalId];
                const sharePrice = Number(share.price || 0);

                return (
                  <div key={share.id} className="glass rounded-2xl overflow-hidden border border-gold-500/30 flex flex-col md:flex-row relative hover:bg-navy-800/40 transition-colors shadow-xl shadow-forest-900/20">
                     
                     {/* Thumbnail Area */}
                     <div className="w-full md:w-32 h-32 md:h-auto shrink-0 bg-navy-900/80 relative flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-gold-500/30">
                        {animal?.photoUrl ? (
                           <img src={animal.photoUrl} alt={animal.tagNo} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                        ) : (
                           <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-20">🐄</div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950 to-transparent p-2 text-center">
                           <span className="text-white font-black text-xl drop-shadow-md">{animal?.tagNo || '?'}</span>
                        </div>
                        {/* Ozel Hissem Rozeti */}
                        <div className="absolute top-2 left-2 md:hidden">
                           <span className="bg-gold-500 text-navy-950 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded shadow-xl">
                              <Beef className="w-3 h-3" /> BENİM HİSSEM
                           </span>
                        </div>
                     </div>

                     {/* Details Area */}
                     <div className="p-4 flex-1 flex flex-col justify-between relative z-10 hidden md:flex md:absolute md:top-2 md:right-2">
                         <span className="bg-gold-500 text-navy-950 text-[10px] items-center gap-1 font-bold px-2 py-0.5 rounded shadow-xl inline-flex w-max ml-auto">
                              <Beef className="w-3 h-3" /> BENİM HİSSEM
                         </span>
                     </div>
                     <div className="p-4 flex-1 flex flex-col justify-between relative z-10 w-full">
                        
                        <div className="flex flex-wrap gap-2 text-[11px] mb-4 mt-1 md:mt-4">
                           <div className="bg-navy-900/60 p-2 rounded-lg border border-navy-700/50 flex flex-col min-w-[70px]">
                              <span className="text-navy-400">Sıra</span>
                              <span className="text-white font-bold text-sm">{animal?.slaughterNo || '-'}</span>
                           </div>
                           <div className="bg-navy-900/60 p-2 rounded-lg border border-navy-700/50 flex flex-col min-w-[70px]">
                              <span className="text-navy-400">Ağırlık</span>
                              <span className="text-white font-bold text-sm">{animal?.weight ? `${animal.weight} KG` : '-'}</span>
                           </div>
                           <div className="bg-gradient-to-br from-gold-500/10 to-gold-700/10 p-2 rounded-lg border border-gold-500/30 flex flex-col flex-1 pl-3 border-l-4 border-l-gold-500">
                              <span className="text-gold-400/80">Hisse Bedelim</span>
                              <span className="text-gold-400 font-black text-lg">₺{sharePrice.toLocaleString('tr-TR')}</span>
                           </div>
                        </div>

                        {/* Action Button */}
                        <button
                           onClick={() => handleOpenTicketModal(animal)}
                           className="w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all bg-navy-800 hover:bg-forest-600 text-white border border-navy-600 shadow-md hover:shadow-forest-600/30 relative active:scale-95"
                        >
                           <MessageCircle className="w-5 h-5" />
                           {tickets.find(t => t.animalId === animal?.id) ? 'Destek Talebini Görüntüle' : 'Sorularınız Mı Var? Bize Yazın'}
                           
                           {tickets.find(t => t.animalId === animal?.id && t.status === 'resolved') && (
                             <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-gold-400 rounded-full border-2 border-navy-800 animate-pulse shadow-lg"></span>
                           )}
                        </button>
                     </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );

  // ── RENDER: KATALOG ──
  const renderKatalog = () => (
    <>
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-gold-400" />
            Kurbanlık Kataloğu
          </h2>
          <p className="text-navy-400 mt-1 text-sm">
            Sistemdeki tüm kurbanlıkları ve boş hisse durumlarını şeffaf bir şekilde inceleyebilirsiniz.
          </p>
        </div>
      </div>

      {catalogLoading ? (
         <div className="flex flex-col items-center justify-center p-12 text-navy-400">
            <Loader2 className="w-8 h-8 animate-spin mb-4 text-gold-500" />
            <p>Katalog güncelleniyor...</p>
         </div>
      ) : catalogAnimals.length === 0 ? (
         <div className="glass rounded-2xl p-12 text-center text-navy-300">
            Sistemde henüz kurbanlık bulunmuyor.
         </div>
      ) : (
         <>
            {/* ── Dynamic Branch Filter ── */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-2 hide-scrollbar">
               {['all', ...Array.from(new Set(catalogAnimals.map(a => getParentBranchName(a.branch, systemSettings?.systemBranches))))].map(branch => (
                  <button
                     key={branch}
                     onClick={() => setSelectedCatalogBranch(branch)}
                     className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-md ${
                        selectedCatalogBranch === branch 
                        ? 'bg-gold-500 text-navy-950 scale-105 border border-gold-400' 
                        : 'bg-navy-800/80 text-navy-300 hover:text-white border border-navy-700 hover:bg-navy-700'
                     }`}
                  >
                     {branch === 'all' ? 'Tüm Şubeler' : branch}
                  </button>
               ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
               {catalogAnimals
                  .filter(a => selectedCatalogBranch === 'all' || getParentBranchName(a.branch, systemSettings?.systemBranches) === selectedCatalogBranch)
                  .map(animal => {
                  const isFull = animal.remainingShares <= 0;
                  const isLastOne = animal.remainingShares === 1;
                  const isLastTwo = animal.remainingShares === 2;
                  
                  return (
                  <div key={animal.id} className="glass rounded-2xl overflow-hidden border border-navy-700/60 flex relative hover:bg-navy-800/40 transition-colors shadow-lg hover:shadow-gold-500/10">
                     
                     {/* Thumbnail Area */}
                     <div className="w-28 sm:w-32 shrink-0 bg-navy-900/80 relative flex flex-col items-center justify-center border-r border-navy-700/50">
                        {animal.photoUrl ? (
                           <img src={animal.photoUrl} alt={animal.tagNo} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                        ) : (
                           <div className="absolute inset-0 flex items-center justify-center text-4xl opacity-20">🐄</div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950 to-transparent p-2 text-center">
                           <span className="text-white font-black text-lg drop-shadow-md">{animal.tagNo}</span>
                        </div>
                     </div>

                     {/* Details Area */}
                     <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between relative z-10">
                        
                        {/* Status Rose */}
                        <div className="flex justify-between items-start mb-2">
                           {/* Şube Etiketi */}
                           <span className="text-navy-300 text-[10px] font-bold bg-navy-900/50 px-2 py-0.5 rounded border border-navy-700/50 flex items-center gap-1">
                               <span className="text-red-400">📍</span> {getParentBranchName(animal.branch, systemSettings?.systemBranches)}
                           </span>

                           {/* FOMO Aciliyet Rozetleri */}
                           {isFull ? (
                              <span className="bg-navy-800 text-navy-500 border border-navy-700 text-[10px] font-bold px-2 py-0.5 rounded-full">TÜKENDİ</span>
                           ) : isLastOne ? (
                              <span className="bg-danger-500/90 text-white border border-danger-400 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.6)]">
                                 <ShieldAlert className="w-3 h-3" /> 🚨 SADECE SON HİSSE!
                              </span>
                           ) : isLastTwo ? (
                              <span className="bg-gold-500/90 text-navy-950 border border-gold-400 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(234,179,8,0.5)]">
                                 <Tag className="w-3 h-3" /> 🔥 SON 2 HİSSE
                              </span>
                           ) : (
                              <span className="bg-forest-500/20 text-forest-300 border border-forest-500/30 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full shadow-md shadow-forest-500/10 transition-all hover:bg-forest-500/30">
                                 <Tag className="w-3 h-3" /> {animal.remainingShares} BOŞ HİSSE
                              </span>
                           )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2 text-[11px] mb-3">
                           <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col">
                              <span className="text-navy-400">Sıra</span>
                              <span className="text-white font-bold">{animal.slaughterNo || '-'}</span>
                           </div>
                           <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col">
                              <span className="text-navy-400">Ağırlık</span>
                              <span className="text-white font-bold">{animal.weight ? `${animal.weight} KG` : '-'}</span>
                           </div>
                           <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col flex-1 pl-3 border-l-2 border-l-gold-500">
                              <span className="text-navy-400">Hisse Fiyatı</span>
                              <span className="text-gold-400 font-black text-sm">₺{(Number(animal.price || 0) / Number(animal.totalShares || 1)).toLocaleString('tr-TR')}</span>
                           </div>
                        </div>

                        {/* Action Button */}
                        <button
                           onClick={() => handleWhatsAppClick(animal, true)}
                           disabled={isFull}
                           className={`w-full py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                              isFull 
                              ? 'bg-navy-800 text-navy-500 cursor-not-allowed border border-navy-700' 
                              : 'bg-gold-500 text-navy-950 hover:bg-gold-400 shadow-lg shadow-gold-500/20 active:scale-95'
                           }`}
                        >
                           <MessageCircle className="w-4 h-4" />
                           {isFull ? 'Tükendi' : 'Bilgi Al'}
                        </button>
                     </div>
                  </div>
               )
            })}
         </div>
         </>
      )}
    </>
  )

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col relative text-white">
      {/* ── Deep Blue Background ── */}
      <div className="fixed inset-0 z-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950 pointer-events-none"></div>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 glass border-b border-navy-700/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg border border-white/10 bg-navy-950/50 flex-shrink-0 flex items-center justify-center">
                 <img src="/images/angry_bull_logo.png" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-sm font-extrabold text-white leading-tight tracking-wide">
                  HAŞİMOĞLU ÇİFTLİĞİ
                </h1>
                <p className="text-[10px] text-forest-300 font-bold uppercase tracking-widest">Müşteri Portalı</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 relative z-10">
              <Link
                to="/"
                className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-teal-300/80 hover:text-white bg-white/0 hover:bg-white/[0.06] border border-transparent hover:border-teal-400/20 transition-all duration-300 font-medium"
                title="Ana Sayfaya Dön"
              >
                <Home className="w-4 h-4 transition-transform group-hover:scale-110" />
                <span className="hidden sm:inline tracking-wide">Siteye Dön</span>
              </Link>
              <button
                onClick={signOut}
                title="Güvenli Çıkış"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-white bg-danger-500/10 hover:bg-danger-500/30 border border-danger-500/20 transition-all font-bold"
              >
                <LogOut className="w-4 h-4 text-danger-400" />
                <span className="hidden sm:inline">Çıkış Yap</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-6 pb-24 relative z-10">
         {activeTab === 'hesabim' ? renderHesabim() : renderKatalog()}
      </main>

      {/* ── Mobile Bottom Navigation (Sticky Bottom Bar) ── */}
      <nav className="fixed bottom-4 left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:w-[400px] z-50 glass rounded-2xl border border-navy-500/30 shadow-2xl p-1 flex items-center justify-between shadow-black/50 overflow-hidden">
         <div className="absolute inset-0 bg-navy-950/80 backdrop-blur-xl"></div>
         <button
            onClick={() => setActiveTab('hesabim')}
            className={`relative z-10 flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-300 ${
               activeTab === 'hesabim' 
               ? 'bg-gradient-to-b from-forest-500 to-forest-700 text-white shadow-lg' 
               : 'text-navy-400 hover:text-white'
            }`}
         >
            <User className={`w-5 h-5 mb-1 ${activeTab === 'hesabim' ? 'scale-110' : ''} transition-transform`} /> 
            <span className="text-[10px] font-bold uppercase tracking-wider">Hesabım</span>
         </button>
         
         {!systemSettings?.isCatalogClosed && (
            <button
               onClick={() => setActiveTab('katalog')}
               className={`relative z-10 flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-300 ${
                  activeTab === 'katalog' 
                  ? 'bg-gradient-to-b from-gold-500 to-gold-600 text-navy-950 shadow-lg' 
                  : 'text-navy-400 hover:text-white'
               }`}
            >
               <ShoppingCart className={`w-5 h-5 mb-1 ${activeTab === 'katalog' ? 'scale-110' : ''} transition-transform`} /> 
               <span className="text-[10px] font-bold uppercase tracking-wider">Katalog</span>
            </button>
         )}
      </nav>

      {/* Ticket Modal */}
      <CustomerTicketModal 
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
        animal={selectedAnimalForTicket}
        existingTicket={tickets.find(t => t.animalId === selectedAnimalForTicket?.id)}
      />
    </div>
  );
}
